import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { PageHeader, DataTable, Td, StatusBadge, ActionButton, EmptyState } from "../../components/common";
import { JobTicketForm } from "../../components/JobTicketForm";

const JOB_STATUS_COLORS = {
  "Active": "text-emerald-400",
  "Completed": "text-slate-400",
  "Under Review": "text-yellow-400",
  "Scheduled": "text-blue-400",
  "Assigned to Service Provider": "text-purple-400",
  "Paused": "text-orange-400",
  "Cancelled": "text-slate-500",
  "open": "text-emerald-400",
  "in_progress": "text-blue-400",
};

const TICKET_COLUMNS = ["Ticket", "Division", "Priority", "Operator", "Job Status", "Resource Type", "Activity", "Item", "Qty", "Pickup", "Destination", "Start", "Approval", "Submitted / By", ""];

export default function SwdJobTickets() {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/swd/job-tickets").then((r) => setTickets(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setOpen(true);
  };

  return (
    <div data-testid="swd-tickets-page">
      <PageHeader title="Job Tickets" subtitle="Field tickets per job — division, resources, schedule & route" testId="swd-tickets-header">
        <ActionButton testId="swd-create-ticket-button" onClick={openCreate}>
          <Plus size={16} /> New Ticket
        </ActionButton>
      </PageHeader>

      {tickets.length === 0 ? (
        <EmptyState message="No job tickets yet" testId="swd-tickets-empty" />
      ) : (
        <DataTable
          testId="swd-tickets-table"
          columns={TICKET_COLUMNS}
          rows={tickets}
          renderRow={(t) => (
            <tr key={t.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`ticket-row-${t.id}`}>
              <Td className="font-mono text-amber-400">{t.ticket_number}</Td>
              <Td className="font-medium text-slate-100">{t.division_name || "—"}</Td>
              <Td>{t.priority || "—"}</Td>
              <Td>{t.operator || "—"}</Td>
              <Td>
                <span
                  data-testid={`ticket-status-${t.id}`}
                  className={`text-sm font-medium ${JOB_STATUS_COLORS[t.job_status] || "text-slate-300"}`}
                >
                  {t.job_status || "Active"}
                </span>
              </Td>
              <Td>{t.resource_type || "—"}</Td>
              <Td>{t.activity || "—"}</Td>
              <Td>{t.item || "—"}</Td>
              <Td className="font-mono">{t.qty ?? "—"}</Td>
              <Td className="max-w-[140px] truncate">{t.pickup_location || "—"}</Td>
              <Td className="max-w-[140px] truncate">{t.destination || "—"}</Td>
              <Td className="text-xs text-slate-500">{t.start ? fmtDate(t.start) : "—"}</Td>
              <Td><StatusBadge status={t.approval_status || "Not Approved"} /></Td>
              <Td className="text-xs text-slate-500">
                {fmtDate(t.submitted)}
                {t.created_by && (
                  <>
                    <br />
                    <span className="text-slate-600">{t.created_by}</span>
                  </>
                )}
              </Td>
              <Td>
                <button
                  data-testid={`ticket-edit-${t.id}`}
                  onClick={() => openEdit(t)}
                  title={t.last_updated ? `Last updated ${fmtDate(t.last_updated)}` : ""}
                  className="p-1.5 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors duration-200"
                >
                  <Pencil size={14} />
                </button>
              </Td>
            </tr>
          )}
        />
      )}

      <JobTicketForm open={open} onOpenChange={setOpen} onSaved={load} editing={editing} defaultStatus="Active" />
    </div>
  );
}
